/**
 * Stream Connection Pool
 * WebRTC 연결 풀 관리자
 * 연결 재사용, 품질 관리, 리소스 최적화를 담당
 */

import { MultiDeviceWebRTCConnection } from './WebRTCConnection';
import type {
  CameraChannel,
  StreamQuality,
  PooledConnection,
  PoolStats,
} from './types';
import {
  startLiveStreamingWorkflow,
  changeLiveDirectionWithRetry,
  stopLiveView
} from '../live/api';

// Channel mapping for FleetA API
const CHANNEL_MAPPING = {
  front: '1',
  rear: '2', 
  interior: '3',
  exterior: '4'
} as const;

/**
 * Pool configuration interface
 */
export interface PoolConfig {
  enablePooling: boolean;
  connectionTimeout: number;
}

/**
 * Stream Connection Pool Class
 * WebRTC 연결들을 효율적으로 관리하는 풀
 */
export class StreamConnectionPool {
  // Pool identification
  private psn: string;
  private config: PoolConfig;
  
  // Connection pool
  private connections: Map<string, PooledConnection> = new Map();
  private connectionLimits: Record<StreamQuality, number> = { high: 1, medium: 2, low: 4 };
  
  // Pool management
  private isInitialized: boolean = false;
  private cleanupTimer: NodeJS.Timeout | null = null;
  
  // Channel mapping for FleetA API
  private channelMapping: Record<CameraChannel, string> = {
    front: '1',
    rear: '2',
    interior: '3',
    exterior: '4'
  };

  /**
   * Create Stream Connection Pool instance
   * @param psn - Device PSN
   */
  constructor(psn: string, config: PoolConfig) {
    this.psn = psn;
    this.config = config;
  }

  /**
   * Initialize the connection pool
   * 연결 풀 초기화
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    try {
      // Start cleanup timer if pooling is enabled
      if (this.config.enablePooling) {
        this.startCleanupTimer();
      }
      
      this.isInitialized = true;
      
    } catch (error) {
      const errorMessage = `Failed to initialize pool for ${this.psn}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      throw new Error(errorMessage);
    }
  }

  /**
   * Get connection from pool or create new one
   * 풀에서 연결 가져오기 또는 새로 생성
   * @param channel - Camera channel
   * @param quality - Stream quality
   * @returns WebRTC connection instance
   */
  async getConnection(channel: CameraChannel, quality: StreamQuality): Promise<MultiDeviceWebRTCConnection> {
    if (!this.isInitialized) {
      throw new Error(`Pool for ${this.psn} not initialized`);
    }
    
    const connectionKey = `${this.psn}_${channel}_${quality}`;
    
    // Check if connection exists in pool and is active
    if (this.config.enablePooling && this.connections.has(connectionKey)) {
      const pooled = this.connections.get(connectionKey)!;
      if (pooled.connection.getState() === 'connected') {
        pooled.lastUsed = Date.now();
        pooled.isActive = true;
        return pooled.connection;
      } else {
        // Remove invalid connection
        await this.removeConnection(connectionKey);
      }
    }
    
    // Check connection limits for quality
    const qualityConnections = this.getConnectionsByQuality(quality);
    if (qualityConnections.length >= this.connectionLimits[quality]) {
      await this.evictOldestConnection(quality);
    }
    
    // Create new connection
    const connection = await this.createConnection(channel, quality);
    
    // Store in pool if pooling is enabled
    if (this.config.enablePooling) {
      this.connections.set(connectionKey, {
        connection,
        channel,
        quality,
        createdAt: Date.now(),
        lastUsed: Date.now(),
        isActive: true
      });
    }
    
    return connection;
  }

  /**
   * Change device channel via FleetA API
   * FleetA API를 통한 디바이스 채널 변경
   * @param newChannel - New channel to switch to
   */
  async changeDeviceChannel(newChannel: CameraChannel): Promise<void> {
    const channelParam = this.channelMapping[newChannel];
    
    try {
      await changeLiveDirectionWithRetry({
        psn: this.psn,
        param1: channelParam as import('../live/types').LiveCameraChannel
      });
      
    } catch (error) {
      const errorMessage = `Failed to change device channel for ${this.psn}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      throw new Error(errorMessage);
    }
  }

  /**
   * Get pool statistics
   * 풀 통계 조회
   * @returns Pool statistics
   */
  getPoolStats(): PoolStats {
    const totalConnections = this.connections.size;
    const activeConnections = Array.from(this.connections.values()).filter(p => p.isActive).length;
    const idleConnections = totalConnections - activeConnections;
    
    const connectionsByQuality: Record<StreamQuality, number> = {
      high: this.getConnectionsByQuality('high').length,
      medium: this.getConnectionsByQuality('medium').length,
      low: this.getConnectionsByQuality('low').length
    };
    
    return {
      totalConnections,
      activeConnections,
      idleConnections,
      memoryUsage: this.calculateMemoryUsage(),
      bandwidthUsage: this.calculateBandwidthUsage(),
      connectionsByQuality
    };
  }

  /**
   * Destroy pool and cleanup all connections
   * 풀 파괴 및 모든 연결 정리
   */
  async destroy(): Promise<void> {
    try {
      // Stop cleanup timer
      this.stopCleanupTimer();
      
      // Disconnect all connections
      const disconnectPromises = Array.from(this.connections.values()).map(pooled =>
        pooled.connection.disconnect()
      );
      
      await Promise.allSettled(disconnectPromises);
      
      // Clear pool
      this.connections.clear();
      this.isInitialized = false;
      
    } catch (error) {
    }
  }

  // ========================
  // PRIVATE METHODS
  // ========================

  /**
   * Create new WebRTC connection
   * 새 WebRTC 연결 생성
   * @param channel - Camera channel
   * @param quality - Stream quality
   * @returns WebRTC connection instance
   */
  private async createConnection(channel: CameraChannel, quality: StreamQuality): Promise<MultiDeviceWebRTCConnection> {
    try {
      // Get WebRTC configuration from FleetA API
      const channelParam = this.channelMapping[channel];
      const webrtcConfig = await startLiveStreamingWorkflow(
        this.psn,
        channelParam as import('../live/types').LiveCameraChannel,
        `${this.psn}_${channel}_${quality}`,
        'VIEWER'
      );
      
      // Create connection with configuration
      const connection = new MultiDeviceWebRTCConnection({
        deviceId: this.psn,
        channel: channel,
        quality: quality,
        clientId: `${this.psn}_${channel}_${quality}`,
        role: 'VIEWER',
        enableStats: true,
        statsInterval: 2000,
        enableVoiceComm: true,
        onStateChange: (state) => this.handleConnectionStateChange(channel, state),
        onError: (error) => this.handleConnectionError(channel, error),
        onStreamReceived: (stream) => this.handleStreamReceived(channel, stream),
        onStatsUpdate: (stats) => this.handleStatsUpdate(channel, stats),
        onVoiceCommStateChange: (voiceState) => this.handleVoiceCommStateChange(channel, voiceState)
      });
      
      // Connect to WebRTC
      await connection.connect();
      
      return connection;
      
    } catch (error) {
      const errorMessage = `Failed to create connection for ${this.psn}/${channel}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      throw new Error(errorMessage);
    }
  }

  /**
   * Get connections by quality level
   * 품질별 연결 목록 조회
   * @param quality - Stream quality
   * @returns Array of pooled connections
   */
  private getConnectionsByQuality(quality: StreamQuality): PooledConnection[] {
    return Array.from(this.connections.values()).filter(pooled => pooled.quality === quality);
  }

  /**
   * Evict oldest connection of specified quality
   * 지정된 품질의 가장 오래된 연결 제거
   * @param quality - Stream quality
   */
  private async evictOldestConnection(quality: StreamQuality): Promise<void> {
    const qualityConnections = this.getConnectionsByQuality(quality)
      .sort((a, b) => a.lastUsed - b.lastUsed);
    
    if (qualityConnections.length > 0) {
      const oldestConnection = qualityConnections[0];
      const connectionKey = this.findConnectionKey(oldestConnection);
      
      if (connectionKey) {
        await this.removeConnection(connectionKey);
      }
    }
  }

  /**
   * Remove connection from pool
   * 풀에서 연결 제거
   * @param connectionKey - Connection key to remove
   */
  private async removeConnection(connectionKey: string): Promise<void> {
    const pooled = this.connections.get(connectionKey);
    if (pooled) {
      await pooled.connection.disconnect();
      this.connections.delete(connectionKey);
    }
  }

  /**
   * Find connection key for a pooled connection
   * 풀된 연결의 키 찾기
   * @param targetConnection - Target pooled connection
   * @returns Connection key or null
   */
  private findConnectionKey(targetConnection: PooledConnection): string | null {
    for (const [key, connection] of this.connections) {
      if (connection === targetConnection) {
        return key;
      }
    }
    return null;
  }

  /**
   * Calculate total memory usage
   * 총 메모리 사용량 계산
   * @returns Memory usage in bytes
   */
  private calculateMemoryUsage(): number {
    let totalMemory = 0;
    
    this.connections.forEach(pooled => {
      // Estimate memory per connection based on quality
      switch (pooled.quality) {
        case 'high':
          totalMemory += 20 * 1024 * 1024; // 20MB
          break;
        case 'medium':
          totalMemory += 15 * 1024 * 1024; // 15MB
          break;
        case 'low':
          totalMemory += 10 * 1024 * 1024; // 10MB
          break;
      }
    });
    
    return totalMemory;
  }

  /**
   * Calculate total bandwidth usage
   * 총 대역폭 사용량 계산
   * @returns Bandwidth usage in bytes/sec
   */
  private calculateBandwidthUsage(): number {
    let totalBandwidth = 0;
    
    this.connections.forEach(pooled => {
      if (pooled.isActive) {
        // Estimate bandwidth per connection based on quality
        switch (pooled.quality) {
          case 'high':
            totalBandwidth += 5000000 / 8; // 5Mbps to bytes/sec
            break;
          case 'medium':
            totalBandwidth += 2500000 / 8; // 2.5Mbps to bytes/sec
            break;
          case 'low':
            totalBandwidth += 1000000 / 8; // 1Mbps to bytes/sec
            break;
        }
      }
    });
    
    return totalBandwidth;
  }

  /**
   * Start cleanup timer for idle connections
   * 유휴 연결 정리 타이머 시작
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) return;
    
    this.cleanupTimer = setInterval(() => {
      this.cleanupIdleConnections();
    }, 60000); // Cleanup every minute
  }

  /**
   * Stop cleanup timer
   * 정리 타이머 중지
   */
  private stopCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Cleanup idle connections
   * 유휴 연결 정리
   */
  private cleanupIdleConnections(): void {
    const now = Date.now();
    const ttl = 300000; // 5 minutes
    const connectionsToRemove: string[] = [];
    
    this.connections.forEach((pooled, key) => {
      if (!pooled.isActive && (now - pooled.lastUsed) > ttl) {
        connectionsToRemove.push(key);
      }
    });
    
    // Remove idle connections
    connectionsToRemove.forEach(key => {
      this.removeConnection(key);
    });
  }

  // ========================
  // EVENT HANDLERS
  // ========================

  private handleConnectionStateChange(channel: CameraChannel, state: import('./types').ConnectionState): void {
  }

  private handleConnectionError(channel: CameraChannel, error: Error): void {
  }

  private handleStreamReceived(channel: CameraChannel, stream: MediaStream): void {
  }

  private handleStatsUpdate(channel: CameraChannel, stats: any): void {
    // Handle connection statistics update
    // This can be used to update bandwidth calculations
  }
  
  private handleVoiceCommStateChange(channel: CameraChannel, voiceState: import('./types').VoiceCommState): void {
  }
}